import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import Navigation from '../components/Navigation';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import {
  IndianRupee, Briefcase, FileText, CheckCircle, Plus, Download,
  Clock, AlertCircle, Check, X, Wallet
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ClientDashboard = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [projects, setProjects] = useState([]);
  const [contracts, setContracts] = useState([]);
  const [pendingMilestones, setPendingMilestones] = useState([]);
  const [walletBalance, setWalletBalance] = useState(0);
  const [loading, setLoading] = useState(true);
  const [showProjectDialog, setShowProjectDialog] = useState(false);
  const [newProject, setNewProject] = useState({
    title: '', description: '', category: '', budget_min: '', budget_max: '', skills_required: '', deadline: '',
  });

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      const [projectsRes, contractsRes, milestonesRes, walletRes] = await Promise.all([
        axios.get(`${API}/projects/my`),
        axios.get(`${API}/contracts`),
        axios.get(`${API}/client/milestones/pending`).catch(() => ({ data: [] })),
        axios.get(`${API}/wallet`).catch(() => ({ data: { balance: 0 } })),
      ]);
      setProjects(projectsRes.data);
      setContracts(contractsRes.data.filter(c => c.client_id === user.id));
      setPendingMilestones(milestonesRes.data);
      setWalletBalance(walletRes.data?.balance || 0);
    } catch (error) {
      console.error('Failed to fetch data', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateProject = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/projects`, {
        ...newProject,
        budget_min: parseFloat(newProject.budget_min),
        budget_max: parseFloat(newProject.budget_max),
        skills_required: newProject.skills_required.split(',').map(s => s.trim()),
      });
      toast.success('Project posted successfully!');
      setShowProjectDialog(false);
      setNewProject({ title: '', description: '', category: '', budget_min: '', budget_max: '', skills_required: '', deadline: '' });
      fetchData();
    } catch {
      toast.error('Failed to post project');
    }
  };

  const handleApproveMilestone = async (milestoneId) => {
    try {
      await axios.patch(`${API}/milestones/${milestoneId}/client-approve`);
      toast.success('Milestone approved! Awaiting admin release.');
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to approve');
    }
  };

  const handleDownloadInvoice = (contractId) => {
    window.open(`${API}/invoices/${contractId}`, '_blank');
  };

  const totalSpent = contracts
    .filter(c => ['escrowed', 'released'].includes(c.payment_status))
    .reduce((sum, c) => sum + c.amount, 0);
  const activeContracts = contracts.filter(c => c.status === 'active').length;
  const completedContracts = contracts.filter(c => c.status === 'completed').length;

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
        <Navigation />
        <div className="flex items-center justify-center h-96"><div className="text-lg">Loading...</div></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      <Navigation />
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24 py-12">
        <div className="mb-8">
          <h1 className="text-3xl md:text-4xl font-heading font-bold text-primary mb-2" data-testid="dashboard-title">
            Client Dashboard
          </h1>
          <p className="text-muted-foreground" data-testid="dashboard-subtitle">Manage your projects and hired talent</p>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8">
          <Card data-testid="stat-spent">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Total Spent</CardTitle>
              <IndianRupee className="h-4 w-4 text-secondary" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-heading font-bold text-primary">₹{totalSpent.toLocaleString('en-IN')}</div>
            </CardContent>
          </Card>
          <Card data-testid="stat-wallet">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Wallet</CardTitle>
              <Wallet className="h-4 w-4 text-secondary" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-heading font-bold text-green-600">₹{walletBalance.toLocaleString('en-IN')}</div>
              <button onClick={() => navigate('/wallet')} className="text-xs text-secondary hover:underline mt-1">View wallet</button>
            </CardContent>
          </Card>
          <Card data-testid="stat-active">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Active Contracts</CardTitle>
              <Briefcase className="h-4 w-4 text-secondary" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-heading font-bold text-primary">{activeContracts}</div>
            </CardContent>
          </Card>
          <Card data-testid="stat-projects">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Projects Posted</CardTitle>
              <FileText className="h-4 w-4 text-secondary" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-heading font-bold text-primary">{projects.length}</div>
            </CardContent>
          </Card>
          <Card data-testid="stat-completed">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Completed</CardTitle>
              <CheckCircle className="h-4 w-4 text-secondary" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-heading font-bold text-primary">{completedContracts}</div>
            </CardContent>
          </Card>
        </div>

        {/* Pending Milestone Approvals */}
        {pendingMilestones.length > 0 && (
          <Card className="mb-8 border-amber-200 bg-amber-50" data-testid="pending-milestones-section">
            <CardHeader>
              <div className="flex items-center gap-2">
                <AlertCircle className="w-5 h-5 text-amber-600" />
                <CardTitle className="text-lg font-heading text-amber-800">
                  Pending Milestone Approvals ({pendingMilestones.length})
                </CardTitle>
              </div>
              <CardDescription className="text-amber-700">Freelancers are waiting for your approval on these milestones</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {pendingMilestones.map(ms => (
                  <div key={ms.id} className="flex items-center justify-between bg-white rounded-lg p-4 border border-amber-100" data-testid={`pending-milestone-${ms.id}`}>
                    <div>
                      <p className="font-medium text-sm">{ms.title}</p>
                      <p className="text-xs text-muted-foreground">{ms.freelancer_name} · ₹{ms.amount.toLocaleString('en-IN')}</p>
                    </div>
                    <div className="flex gap-2">
                      <Button size="sm" onClick={() => handleApproveMilestone(ms.id)} data-testid={`approve-ms-${ms.id}`}>
                        <Check className="w-3 h-3 mr-1" /> Approve
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => navigate(`/contracts/${ms.contract_id}`)} data-testid={`view-ms-${ms.id}`}>
                        View
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Quick Actions */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
          <Card className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => navigate('/gigs')} data-testid="browse-gigs-card">
            <CardHeader>
              <CardTitle className="text-lg font-heading">Browse Gigs</CardTitle>
              <CardDescription>Find ready-made services from freelancers</CardDescription>
            </CardHeader>
          </Card>
          <Card className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => setShowProjectDialog(true)} data-testid="post-project-card">
            <CardHeader>
              <CardTitle className="text-lg font-heading">Post a Project</CardTitle>
              <CardDescription>Get competitive bids from top talent</CardDescription>
            </CardHeader>
          </Card>
          <Card className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => navigate('/invoices')} data-testid="invoices-card">
            <CardHeader>
              <CardTitle className="text-lg font-heading">GST Invoices</CardTitle>
              <CardDescription>Download PDF invoices for all contracts</CardDescription>
            </CardHeader>
          </Card>
        </div>

        {/* Projects Section */}
        <Card className="mb-8" data-testid="projects-section">
          <CardHeader>
            <div className="flex justify-between items-center">
              <div>
                <CardTitle className="text-2xl font-heading">My Projects</CardTitle>
                <CardDescription>Track your posted projects and their bids</CardDescription>
              </div>
              <Dialog open={showProjectDialog} onOpenChange={setShowProjectDialog}>
                <DialogTrigger asChild>
                  <Button data-testid="create-project-button"><Plus className="w-4 h-4 mr-2" /> Post Project</Button>
                </DialogTrigger>
                <DialogContent className="max-w-2xl">
                  <DialogHeader>
                    <DialogTitle>Post New Project</DialogTitle>
                    <DialogDescription>Get competitive bids from freelancers</DialogDescription>
                  </DialogHeader>
                  <form onSubmit={handleCreateProject} className="space-y-4" data-testid="create-project-form">
                    <div>
                      <Label>Project Title</Label>
                      <Input value={newProject.title} onChange={e => setNewProject({...newProject, title: e.target.value})} required data-testid="project-title-input" placeholder="Build a mobile app" />
                    </div>
                    <div>
                      <Label>Description</Label>
                      <Textarea value={newProject.description} onChange={e => setNewProject({...newProject, description: e.target.value})} required rows={4} data-testid="project-description-input" />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label>Category</Label>
                        <Input value={newProject.category} onChange={e => setNewProject({...newProject, category: e.target.value})} required data-testid="project-category-input" placeholder="Mobile Development" />
                      </div>
                      <div>
                        <Label>Deadline (optional)</Label>
                        <Input type="date" value={newProject.deadline} onChange={e => setNewProject({...newProject, deadline: e.target.value})} data-testid="project-deadline-input" />
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label>Min Budget (₹)</Label>
                        <Input type="number" value={newProject.budget_min} onChange={e => setNewProject({...newProject, budget_min: e.target.value})} required data-testid="project-budget-min-input" />
                      </div>
                      <div>
                        <Label>Max Budget (₹)</Label>
                        <Input type="number" value={newProject.budget_max} onChange={e => setNewProject({...newProject, budget_max: e.target.value})} required data-testid="project-budget-max-input" />
                      </div>
                    </div>
                    <div>
                      <Label>Required Skills (comma separated)</Label>
                      <Input value={newProject.skills_required} onChange={e => setNewProject({...newProject, skills_required: e.target.value})} required data-testid="project-skills-input" placeholder="React, Node.js, MongoDB" />
                    </div>
                    <Button type="submit" className="w-full" data-testid="project-submit-button">Post Project</Button>
                  </form>
                </DialogContent>
              </Dialog>
            </div>
          </CardHeader>
          <CardContent>
            {projects.length === 0 ? (
              <p className="text-muted-foreground text-center py-8" data-testid="no-projects-message">No projects yet. Post your first project to receive bids!</p>
            ) : (
              <div className="space-y-3">
                {projects.map(project => (
                  <div key={project.id} className="border rounded-lg p-4 hover:shadow-sm transition-shadow cursor-pointer" onClick={() => navigate(`/projects/${project.id}`)} data-testid={`project-${project.id}`}>
                    <div className="flex justify-between items-start">
                      <div className="flex-1 min-w-0">
                        <h3 className="font-heading font-semibold mb-1">{project.title}</h3>
                        <p className="text-sm text-muted-foreground mb-2 truncate">{project.description}</p>
                        <div className="flex gap-2 flex-wrap">
                          {project.skills_required?.slice(0, 3).map((skill, i) => <Badge key={i} variant="secondary" className="text-xs">{skill}</Badge>)}
                        </div>
                      </div>
                      <div className="text-right ml-4 flex-shrink-0">
                        <div className="font-heading font-semibold text-secondary text-sm mb-1">₹{project.budget_min?.toLocaleString('en-IN')} - ₹{project.budget_max?.toLocaleString('en-IN')}</div>
                        <Badge
                          variant={project.status === 'open' ? 'default' : 'secondary'}
                          className={
                            project.status === 'pending_approval' ? 'bg-amber-100 text-amber-700 border-amber-300' :
                            project.status === 'open' ? 'bg-green-100 text-green-700 border-green-300' :
                            project.status === 'in_progress' ? 'bg-blue-100 text-blue-700 border-blue-300' :
                            project.status === 'completed' ? 'bg-slate-100 text-slate-600' :
                            project.status === 'cancelled' ? 'bg-red-100 text-red-700 border-red-300' : ''
                          }
                          data-testid={`project-status-${project.id}`}
                        >
                          {project.status === 'pending_approval' ? 'Pending Approval' :
                           project.status === 'open' ? 'Live' :
                           project.status === 'in_progress' ? 'In Progress' :
                           project.status === 'completed' ? 'Completed' :
                           project.status === 'cancelled' ? 'Rejected' : project.status}
                        </Badge>
                        <div className="text-xs text-muted-foreground mt-1 flex items-center justify-end gap-1">
                          <Clock className="w-3 h-3" /> {project.bids_count || 0} bids
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Contracts Section */}
        <Card data-testid="contracts-section">
          <CardHeader>
            <CardTitle className="text-2xl font-heading">Contracts</CardTitle>
            <CardDescription>All your hired freelancers and payment status</CardDescription>
          </CardHeader>
          <CardContent>
            {contracts.length === 0 ? (
              <p className="text-muted-foreground text-center py-8" data-testid="no-contracts-message">No contracts yet. Hire freelancers to get started!</p>
            ) : (
              <div className="space-y-3">
                {contracts.map(contract => {
                  const paidPct = contract.amount > 0 ? Math.min(100, ((contract.total_milestones_paid || 0) / contract.amount) * 100) : 0;
                  return (
                    <div key={contract.id} className="border rounded-lg p-4 hover:shadow-sm transition-shadow" data-testid={`contract-${contract.id}`}>
                      <div className="flex justify-between items-start mb-3">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <h3 className="font-heading font-semibold">{contract.freelancer_name}</h3>
                            <Badge variant={contract.status === 'active' ? 'default' : 'secondary'}>{contract.status}</Badge>
                          </div>
                          <p className="text-sm text-muted-foreground">{contract.type === 'gig' ? 'Gig Order' : 'Project Contract'}</p>
                        </div>
                        <div className="text-right ml-4">
                          <div className="font-heading font-bold text-secondary">₹{contract.amount.toLocaleString('en-IN')}</div>
                          <Badge variant="outline" className="text-xs">{contract.payment_status}</Badge>
                        </div>
                      </div>
                      {contract.milestones_enabled && (
                        <div className="mb-3">
                          <div className="flex justify-between text-xs text-muted-foreground mb-1">
                            <span>Milestone Progress</span>
                            <span>₹{(contract.total_milestones_paid || 0).toLocaleString('en-IN')} / ₹{contract.amount.toLocaleString('en-IN')}</span>
                          </div>
                          <Progress value={paidPct} className="h-1.5" />
                        </div>
                      )}
                      <div className="flex gap-2">
                        <Button size="sm" variant="outline" onClick={() => navigate(`/contracts/${contract.id}`)} data-testid={`view-contract-${contract.id}`}>View</Button>
                        {['escrowed', 'released'].includes(contract.payment_status) && (
                          <Button size="sm" variant="outline" onClick={() => handleDownloadInvoice(contract.id)} data-testid={`download-invoice-${contract.id}`}>
                            <Download className="w-3 h-3 mr-1" /> Invoice
                          </Button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default ClientDashboard;
